import {
  getOrderDisplayStatus,
  type RxUsageStateLike,
} from '~/components/dashboard/clients/utils/orderStatusChipUtils'
import type { OrderDisplayStatus } from '~/constants/orderStatusChip'
import { PrescriptionType } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import i18n from '~/locales/i18n'
import type {
  Prescription,
  PrescriptionV2,
} from '~/types/entities/prescriptions'

import type {
  GetOrderTableRowOptions,
  OrderTableCardHeader,
  OrderTableContent,
  OrderTableFieldRowOptions,
} from './orderTableTypes'
import {
  appendShippingColumn,
  EMPTY_FIELD_PLACEHOLDER,
  getOrderDate,
  getOrderTableRow,
  getOrderTableSubHeader,
  shouldShowOrderAndPrescriptionNumbers,
} from './rxCardUtilities'
import {
  getShippingForUsage,
  type RxUsageWithShipments,
} from './useRxOrderTableShipments'

/** Canonical row input: one table row (including optional shipping and status). */
export interface OrderTableRowInput {
  fillIndex: number
  prescription: Prescription
  /** Per-usage state (Chewy); passed to getOrderDisplayStatus for per-fill chip. */
  rxUsageState?: RxUsageStateLike | null
  shippingPrimary?: string | null
  shippingSecondary?: string | null
  status?: OrderDisplayStatus | null
  totalFills: number
}

export interface RefillsToOrderTableRowInputsOptions {
  numberRefills?: number | null
  quantityNumbRefills?: number | null
}

/**
 * Converts parent + refills into canonical row inputs (no shipping).
 * Fill index: original = 1, then 2, 3… by order date ascending.
 * Returned array is ordered for display: descending by fill number (newest first).
 */
export function refillsToOrderTableRowInputs(
  rx: Prescription,
  refills: Prescription[],
  options: RefillsToOrderTableRowInputsOptions = {},
): OrderTableRowInput[] {
  const numRefillsAllowed = Number(
    options.quantityNumbRefills ?? options.numberRefills ?? 0,
  )
  const totalFills = Math.max(numRefillsAllowed + 1, refills.length + 1)
  const byOrderDateAsc = [rx, ...refills].sort((a, b) => {
    const dateA = getOrderDate(a) ?? ''
    const dateB = getOrderDate(b) ?? ''
    return dateA.localeCompare(dateB)
  })
  const forDisplay = byOrderDateAsc.slice().reverse()
  return forDisplay.map((prescription, i) => ({
    prescription,
    fillIndex: byOrderDateAsc.length - i,
    totalFills,
    shippingPrimary: null,
    shippingSecondary: null,
  }))
}

export interface RxUsagesWithShipmentsDataForConversion {
  id: string
  rxNo?: string | null
  rxUsages?: Array<RxUsageWithShipments | null> | null
}

/** Order date for a usage (for sorting: oldest first = fill 1). */
function getUsageOrderDate(usage: RxUsageWithShipments | null): string {
  return (usage?.rxOrder?.placedAt as string | undefined) ?? ''
}

/**
 * Converts rxUsages (and shipment data) into canonical row inputs with shipping.
 * Total = original + refills = quantityNumbRefills + 1 (e.g. 2 refills → 3).
 * Fill index: oldest = 1 (original), then 2, 3… by order date ascending.
 * Display order: descending (newest first). So 2 rows today → 2/3, 1/3; when 3rd fills → 3/3, 2/3, 1/3.
 */
export function rxUsagesToOrderTableRowInputs(
  rx: Prescription,
  rxUsagesData: RxUsagesWithShipmentsDataForConversion,
): OrderTableRowInput[] {
  const rawUsages = rxUsagesData.rxUsages ?? []
  const usages = rawUsages.filter((u): u is RxUsageWithShipments => u != null)
  if (usages.length === 0) return []

  const numRefillsAllowed =
    (rx as { quantityNumbRefills?: number | null }).quantityNumbRefills ?? 0
  const totalFills = Math.max(numRefillsAllowed + 1, usages.length)
  const byOrderDateAsc = [...usages].sort((a, b) =>
    getUsageOrderDate(a).localeCompare(getUsageOrderDate(b)),
  )

  const rows = byOrderDateAsc.map((usage, i) => {
    const { primary: shippingPrimary, secondary: shippingSecondary } =
      getShippingForUsage(usage)

    const dispensedQty =
      usage?.dispensedAmount != null && usage.dispensedAmount !== ''
        ? Number(usage.dispensedAmount)
        : null
    const syntheticPrescription: Prescription = {
      ...rx,
      id: usage?.externalRxUsageId ?? rx.id,
      creationDate:
        (usage?.rxOrder?.placedAt as string | undefined) ?? rx.creationDate,
      orderNumber: usage?.rxOrder?.externalOrderId ?? rx.orderNumber,
      rxNo: rxUsagesData.rxNo ?? rx.rxNo,
      ...(dispensedQty != null
        ? {
            totalQuantity: dispensedQty,
            quantityPerRefill: dispensedQty,
          }
        : {}),
    } as Prescription

    return {
      prescription: syntheticPrescription,
      fillIndex: i + 1,
      totalFills,
      rxUsageState: usage?.rxUsageState ?? undefined,
      shippingPrimary,
      shippingSecondary,
    }
  })

  return rows.reverse()
}

export interface OrderTableRowInputsToContentOptions extends OrderTableFieldRowOptions {
  convertedLoadedRx?: Prescription
  isOrderedDrug?: boolean
  loadedSameAsRx?: boolean
  /** When set (e.g. after expanding order table), use so refills and full prescription are available for building rows when not using rxUsages. */
  orderTableLoadedRx?: PrescriptionV2 | null
  parentRx: Prescription
  prescriptionType: PrescriptionType | null | undefined
  /** When set (Chewy) and rxUsages present, table is built from rxUsages instead of refills. */
  rxUsagesData?: RxUsagesWithShipmentsDataForConversion | null
  timezone: string
}

/**
 * Builds OrderTableContent from canonical row inputs.
 * Uses getOrderTableRow for cells, derives card headers from prescription, appends shipping when present.
 */
export function orderTableRowInputsToContent(
  rowInputs: OrderTableRowInput[],
  options: OrderTableRowInputsToContentOptions,
): OrderTableContent {
  const {
    parentRx,
    convertedLoadedRx,
    loadedSameAsRx = false,
    prescriptionType,
    timezone,
    ...opts
  } = options
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const isOrderedDrug =
    options.isOrderedDrug ??
    (parentRx as { type?: string }).type === OrderType.INVENTORY

  const rowInputsWithStatus: OrderTableRowInput[] = rowInputs.map((r) => ({
    ...r,
    status:
      r.status ??
      getOrderDisplayStatus({
        prescription: r.prescription,
        prescriptionStates: opts.prescriptionStates,
        retailOrderStates: opts.retailOrderStates,
        rxUsageState: r.rxUsageState ?? undefined,
      }),
  }))

  const baseRowOptions: Omit<
    GetOrderTableRowOptions,
    'fillIndex' | 'parentForQty' | 'parentPrescription' | 'qtyRx' | 'totalFills'
  > = {
    ...opts,
    isInHouse: isInHouse || isOrderedDrug,
    isOrderedDrug,
    prescriptionType:
      prescriptionType ?? (isOrderedDrug ? PrescriptionType.IN_HOUSE : null),
    timezone,
  }

  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const rows = rowInputsWithStatus.map((rowInput) => {
    const { prescription, fillIndex, totalFills } = rowInput
    const isParentById = prescription.id === parentRx.id
    const isParentRow = isChewy ? fillIndex === 1 : isParentById
    const rowOptions: GetOrderTableRowOptions = {
      ...baseRowOptions,
      fillIndex,
      totalFills,
      parentForQty: isParentRow ? undefined : (convertedLoadedRx ?? parentRx),
      parentPrescription: isParentRow ? undefined : parentRx,
      qtyRx:
        !isChewy && isParentRow && loadedSameAsRx
          ? convertedLoadedRx
          : undefined,
    }
    return getOrderTableRow(prescription, rowOptions)
  })

  const firstRow = rows[0] ?? []
  const headers = firstRow.map((cell) => cell.label)

  const parentHeader = getOrderTableSubHeader(parentRx, prescriptionType)

  const cardHeaders: OrderTableCardHeader[] = rowInputsWithStatus.map(
    (rowInput) => {
      const { prescription, fillIndex } = rowInput
      const status = rowInput.status ?? null
      const isParentById = prescription.id === parentRx.id
      const isParentRow = isChewy ? fillIndex === 1 : isParentById
      const headerPrescriptionType = isParentRow
        ? prescriptionType
        : (
            prescription as Prescription & {
              prescriptionType?: PrescriptionType | null
            }
          ).prescriptionType
      const showOrderAndPrescriptionNumbers =
        shouldShowOrderAndPrescriptionNumbers(headerPrescriptionType)

      if (isParentRow) {
        return {
          text: parentHeader ?? i18n.t('Common:ORDER'),
          status,
          orderNumber: parentRx.orderNumber ?? EMPTY_FIELD_PLACEHOLDER,
          rxNo: parentRx.rxNo ?? EMPTY_FIELD_PLACEHOLDER,
          ...(showOrderAndPrescriptionNumbers
            ? { showOrderAndPrescriptionNumbers: true }
            : {}),
        }
      }

      const refillHeader = getOrderTableSubHeader(
        prescription,
        (
          prescription as Prescription & {
            prescriptionType?: PrescriptionType | null
          }
        ).prescriptionType,
      )
      const dispensedFromInventory = i18n.t('Common:DISPENSED_FROM_INVENTORY')
      const text =
        refillHeader === dispensedFromInventory
          ? i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.REFILL_DISPENSED')
          : (refillHeader ?? parentHeader ?? i18n.t('Common:ORDER'))

      return {
        text,
        status,
        orderNumber: prescription.orderNumber ?? EMPTY_FIELD_PLACEHOLDER,
        rxNo: prescription.rxNo ?? EMPTY_FIELD_PLACEHOLDER,
        ...(showOrderAndPrescriptionNumbers
          ? { showOrderAndPrescriptionNumbers: true }
          : {}),
      }
    },
  )

  let content: OrderTableContent = {
    cardHeaders,
    headers,
    rows,
  }

  const hasShipping = rowInputs.some(
    (r) =>
      (r.shippingPrimary != null &&
        r.shippingPrimary !== EMPTY_FIELD_PLACEHOLDER) ||
      (r.shippingSecondary != null &&
        r.shippingSecondary !== EMPTY_FIELD_PLACEHOLDER),
  )
  if (hasShipping) {
    const shippingTexts = rowInputs.map((r) => {
      if (r.shippingPrimary != null && r.shippingSecondary != null)
        return [r.shippingPrimary, r.shippingSecondary]
      if (r.shippingPrimary != null) return r.shippingPrimary
      return EMPTY_FIELD_PLACEHOLDER
    })
    content = appendShippingColumn(
      content,
      shippingTexts,
      i18n.t('Common:SHIPPING'),
    )
  }

  return content
}
